import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiSearch, FiPlus, FiEye, FiTrash2, FiTool, FiX, FiCheckCircle, FiList, FiGrid, FiFile, FiFileText } from 'react-icons/fi';
import PinModal from '../components/PinModal';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Repairs = () => {
  const [repairs, setRepairs] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'grid'
  const [formData, setFormData] = useState({ 
    client_id: '', 
    device_type_id: '', 
    device_name: '', 
    brand: '', 
    model: '', 
    serial_number: '', 
    problem_description: '', 
    estimated_cost: '', 
    deposit_amount: '', 
    parts: [] 
  });
  const [newPart, setNewPart] = useState({ name: '', quantity: 1, price: 0 });

  useEffect(() => { 
    fetchRepairs(); 
    fetchDeviceTypes(); 
    fetchStatuses(); 
    fetchClients(); 
  }, [search, statusFilter]);

  const fetchRepairs = async () => {
    try {
      const response = await api.get(`/repairs?search=${search}&status=${statusFilter}`);
      setRepairs(response.data);
    } catch (error) { 
      toast.error('Erreur lors du chargement des réparations'); 
    } finally { 
      setLoading(false); 
    }
  };

  const fetchDeviceTypes = async () => { 
    try { 
      const response = await api.get('/repairs/device-types'); 
      setDeviceTypes(response.data); 
    } catch (error) { console.error(error); } 
  };
  
  const fetchStatuses = async () => { 
    try { 
      const response = await api.get('/repairs/statuses'); 
      setStatuses(response.data); 
    } catch (error) { console.error(error); } 
  };
  
  const fetchClients = async () => { 
    try { 
      const response = await api.get('/clients'); 
      setClients(response.data); 
    } catch (error) { console.error(error); } 
  };

  const resetForm = () => {
    setFormData({ 
      client_id: '', 
      device_type_id: '', 
      device_name: '', 
      brand: '', 
      model: '', 
      serial_number: '', 
      problem_description: '', 
      estimated_cost: '', 
      deposit_amount: '', 
      parts: [] 
    });
    setNewPart({ name: '', quantity: 1, price: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    const submitData = {
      client_id: parseInt(formData.client_id),
      device_type_id: formData.device_type_id ? parseInt(formData.device_type_id) : null,
      device_name: formData.device_name || null,
      brand: formData.brand || null,
      model: formData.model || null,
      serial_number: formData.serial_number || null,
      problem_description: formData.problem_description || null,
      estimated_cost: parseFloat(formData.estimated_cost) || 0,
      deposit_amount: parseFloat(formData.deposit_amount) || 0,
      parts: formData.parts.map(part => ({
        name: part.name,
        quantity: parseInt(part.quantity),
        price: parseFloat(part.price)
      }))
    };
    
    try {
      const response = await api.post('/repairs', submitData);
      toast.success(`Réparation créée avec succès - Ticket: ${response.data.ticket_number}`);
      setShowModal(false);
      resetForm();
      fetchRepairs();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création de la réparation');
    }
  };

  const handleUpdateStatus = async (repairId, newStatusId, notes = '') => {
    try {
      await api.put(`/repairs/${repairId}/status`, { status_id: newStatusId, technician_notes: notes });
      toast.success('Statut mis à jour avec succès');
      fetchRepairs();
      if (selectedRepair) { 
        const response = await api.get(`/repairs/${repairId}`); 
        setSelectedRepair(response.data); 
      }
    } catch (error) { 
      toast.error('Erreur lors de la mise à jour du statut'); 
    }
  };

  const viewDetails = async (repairId) => { 
    try { 
      const response = await api.get(`/repairs/${repairId}`); 
      setSelectedRepair(response.data); 
      setShowDetailModal(true); 
    } catch (error) { 
      toast.error('Erreur lors du chargement des détails'); 
    } 
  };

  const handleDelete = async () => {
    if (!selectedRepair) return;
    try {
      await api.delete(`/repairs/${selectedRepair.id}`);
      toast.success('Réparation supprimée avec succès');
      setShowPinModal(false);
      setSelectedRepair(null);
      fetchRepairs();
    } catch (error) { 
      toast.error('Erreur lors de la suppression'); 
    }
  };

  const addPart = () => {
    if (newPart.name && newPart.price > 0) { 
      setFormData({ 
        ...formData, 
        parts: [...formData.parts, { 
          name: newPart.name, 
          quantity: parseInt(newPart.quantity), 
          price: parseFloat(newPart.price) 
        }] 
      }); 
      setNewPart({ name: '', quantity: 1, price: 0 }); 
    } else { 
      toast.error('Veuillez remplir le nom et le prix de la pièce'); 
    }
  };

  const removePart = (index) => { 
    const newParts = [...formData.parts]; 
    newParts.splice(index, 1); 
    setFormData({ ...formData, parts: newParts }); 
  };

  // Export Excel
  const exportToExcel = () => {
    const exportData = repairs.map(repair => ({
      'Ticket': repair.ticket_number,
      'Client': repair.client_name,
      'Appareil': repair.device_type_name || repair.device_name || 'Non spécifié',
      'Marque/Modèle': `${repair.brand || ''} ${repair.model || ''}`.trim(),
      'Coût estimé (FCFA)': repair.estimated_cost,
      'Acompte (FCFA)': repair.deposit_amount,
      'Statut': repair.status_name,
      'Date création': new Date(repair.created_at).toLocaleDateString('fr-FR')
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Réparations');
    
    const colWidths = [
      { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 15 }
    ];
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `reparations_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  // Export PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // En-tête
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Liste des réparations', pageWidth / 2, 32, { align: 'center' });
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 38, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    
    // Statistiques
    const totalEstimated = repairs.reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
    const totalDeposit = repairs.reduce((sum, r) => sum + (r.deposit_amount || 0), 0);
    
    doc.setFontSize(9);
    doc.text(`Nombre de réparations: ${repairs.length}`, 14, 55);
    doc.text(`Coût total estimé: ${totalEstimated.toLocaleString()} FCFA`, 14, 63);
    doc.text(`Total des acomptes: ${totalDeposit.toLocaleString()} FCFA`, 14, 71);
    
    const tableHeaders = [['Ticket', 'Client', 'Appareil', 'Coût estimé', 'Statut']];
    const tableRows = repairs.slice(0, 50).map(repair => [
      repair.ticket_number,
      repair.client_name,
      repair.device_type_name || repair.device_name || '-',
      `${(repair.estimated_cost || 0).toLocaleString()} FCFA`,
      repair.status_name
    ]);
    
    doc.autoTable({
      startY: 82,
      head: tableHeaders,
      body: tableRows,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 40 },
        2: { cellWidth: 45 },
        3: { cellWidth: 30 },
        4: { cellWidth: 25 }
      }
    });
    
    doc.save(`reparations_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
  const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
  const getStatusColor = (color) => color || '#6c757d';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Réparations</h1>
        <div className="flex gap-3">
          {/* Boutons export */}
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            title="Exporter vers Excel"
          >
            <FiFile size={18} />
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            title="Exporter vers PDF"
          >
            <FiFileText size={18} />
            PDF
          </button>
          {/* Boutons vue */}
          <div className="flex gap-2 border-l pl-3">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
              title="Vue liste"
            >
              <FiList size={18} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
              title="Vue grille"
            >
              <FiGrid size={18} />
            </button>
          </div>
          <button 
            onClick={() => { 
              resetForm();
              setShowModal(true); 
            }} 
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FiPlus size={18} />
            Nouvelle réparation
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Rechercher par ticket, client, marque..." 
              className="input-field pl-10" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </div>
          <select 
            className="input-field" 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            {statuses.map(status => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vue Liste (Tableau) */}
      {viewMode === 'list' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 font-medium text-gray-600">Ticket</th>
                <th className="pb-3 font-medium text-gray-600">Client</th>
                <th className="pb-3 font-medium text-gray-600">Appareil</th>
                <th className="pb-3 font-medium text-gray-600 text-right">Coût estimé</th>
                <th className="pb-3 font-medium text-gray-600 text-center">Statut</th>
                <th className="pb-3 font-medium text-gray-600 text-center">Date</th>
                <th className="pb-3 font-medium text-gray-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {repairs.map((repair) => (
                <tr key={repair.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="py-3 font-medium text-gray-800">{repair.ticket_number}</td>
                  <td className="py-3">{repair.client_name}</td>
                  <td className="py-3">{repair.device_type_name || repair.device_name || '-'}</td>
                  <td className="py-3 text-right text-green-600 font-medium">{formatAmount(repair.estimated_cost)}</td>
                  <td className="py-3 text-center">
                    <span 
                      className="px-2 py-1 rounded-full text-xs text-white"
                      style={{ backgroundColor: getStatusColor(repair.status_color) }}
                    >
                      {repair.status_name}
                    </span>
                  </td>
                  <td className="py-3 text-center text-sm text-gray-500">
                    {new Date(repair.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => viewDetails(repair.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Voir détails"
                      >
                        <FiEye size={16} />
                      </button>
                      <button
                        onClick={() => { 
                          setSelectedRepair(repair); 
                          setShowPinModal(true); 
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                    </td>
                 </tr>
              ))}
            </tbody>
          </table>
          {repairs.length === 0 && (
            <div className="text-center py-12">
              <FiTool className="mx-auto text-gray-300" size={48} />
              <p className="text-gray-500 mt-2">Aucune réparation trouvée</p>
            </div>
          )}
        </div>
      )}

      {/* Vue Grille (Cartes) */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {repairs.map((repair) => (
            <div key={repair.id} className="card">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FiTool className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{repair.ticket_number}</h3>
                    <p className="text-xs text-gray-400">{repair.device_type_name || repair.device_name || 'Appareil'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => viewDetails(repair.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                    <FiEye size={16} />
                  </button>
                  <button onClick={() => { setSelectedRepair(repair); setShowPinModal(true); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-sm font-medium">{repair.client_name}</p>
                <p className="text-xs text-gray-500">{repair.brand} {repair.model}</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-600">{formatAmount(repair.estimated_cost)}</span>
                  <span className="px-2 py-1 rounded-full text-xs text-white" style={{ backgroundColor: getStatusColor(repair.status_color) }}>
                    {repair.status_name}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {repairs.length === 0 && viewMode === 'grid' && (
        <div className="text-center py-12">
          <FiTool className="mx-auto text-gray-300" size={48} />
          <p className="text-gray-500 mt-2">Aucune réparation trouvée</p>
        </div>
      )}

      {/* Modal Nouvelle Réparation */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Nouvelle réparation</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <select 
                  className="input-field" 
                  value={formData.client_id} 
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} 
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name} - {client.phone}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type d'appareil</label>
                  <select 
                    className="input-field" 
                    value={formData.device_type_id} 
                    onChange={(e) => setFormData({ ...formData, device_type_id: e.target.value })}
                  >
                    <option value="">Sélectionner</option>
                    {deviceTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Autre appareil</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    placeholder="Si non listé" 
                    value={formData.device_name} 
                    onChange={(e) => setFormData({ ...formData, device_name: e.target.value })} 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    value={formData.brand} 
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })} 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    value={formData.model} 
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })} 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° série / IMEI</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    value={formData.serial_number} 
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} 
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coût estimé (FCFA)</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    value={formData.estimated_cost} 
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })} 
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description de la panne</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  value={formData.problem_description} 
                  onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })} 
                  placeholder="Décrivez les symptômes..." 
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Acompte (FCFA)</label>
                <input 
                  type="number" 
                  className="input-field" 
                  value={formData.deposit_amount} 
                  onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })} 
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Pièces à remplacer</label>
                <div className="flex gap-2 mb-2">
                  <input 
                    type="text" 
                    placeholder="Nom de la pièce" 
                    className="flex-1 input-field" 
                    value={newPart.name} 
                    onChange={(e) => setNewPart({ ...newPart, name: e.target.value })} 
                  />
                  <input 
                    type="number" 
                    placeholder="Qté" 
                    className="w-20 input-field" 
                    value={newPart.quantity} 
                    onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })} 
                  />
                  <input 
                    type="number" 
                    placeholder="Prix" 
                    className="w-28 input-field" 
                    value={newPart.price} 
                    onChange={(e) => setNewPart({ ...newPart, price: e.target.value })} 
                  />
                  <button type="button" onClick={addPart} className="bg-green-600 text-white px-3 rounded-lg hover:bg-green-700">+</button>
                </div>
                {formData.parts.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    {formData.parts.map((part, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 border-b last:border-0">
                        <span className="text-sm">{part.name} x{part.quantity}</span>
                        <span className="text-sm">{formatAmount(part.price * part.quantity)}</span>
                        <button type="button" onClick={() => removePart(idx)} className="text-red-600 text-sm">Supprimer</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 btn-secondary">Annuler</button>
                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors">Créer la réparation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails Réparation */}
      {showDetailModal && selectedRepair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Réparation {selectedRepair.ticket_number}</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <FiX size={20} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-green-50 rounded-lg">
              <div><p className="text-sm text-gray-500">Client</p><p className="font-medium">{selectedRepair.client_name}</p><p className="text-sm">{selectedRepair.client_phone}</p></div>
              <div><p className="text-sm text-gray-500">Appareil</p><p className="font-medium">{selectedRepair.device_type_name || selectedRepair.device_name || 'Non spécifié'}</p><p className="text-sm">{selectedRepair.brand} {selectedRepair.model}</p></div>
              <div><p className="text-sm text-gray-500">Date création</p><p className="text-sm">{formatDate(selectedRepair.created_at)}</p></div>
              <div><p className="text-sm text-gray-500">Statut actuel</p><span className="px-2 py-1 rounded-full text-xs text-white inline-block" style={{ backgroundColor: selectedRepair.status_color }}>{selectedRepair.status_name}</span></div>
            </div>
            <div className="mb-4"><h3 className="font-semibold mb-2">Description de la panne</h3><p className="text-gray-600">{selectedRepair.problem_description || 'Non spécifiée'}</p></div>
            {selectedRepair.parts && selectedRepair.parts.length > 0 && (
              <div className="mb-4"><h3 className="font-semibold mb-2">Pièces à remplacer</h3><table className="w-full text-sm"><thead className="border-b"><tr><th className="text-left py-1">Pièce</th><th className="text-center py-1">Qté</th><th className="text-right py-1">Total</th></tr></thead><tbody>{selectedRepair.parts.map((part, idx) => (<tr key={idx} className="border-b"><td className="py-1">{part.part_name}</td><td className="text-center py-1">{part.quantity}</td><td className="text-right py-1">{formatAmount(part.total)}</td></tr>))}</tbody></table></div>
            )}
            <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div><p className="text-sm text-gray-500">Coût estimé</p><p className="font-bold text-green-600">{formatAmount(selectedRepair.estimated_cost)}</p></div>
              <div><p className="text-sm text-gray-500">Acompte versé</p><p className="font-medium">{formatAmount(selectedRepair.deposit_amount)}</p></div>
            </div>
            <div className="mb-4"><h3 className="font-semibold mb-2">Suivi de la réparation</h3><div className="flex gap-2 flex-wrap">{statuses.map(status => (<button key={status.id} onClick={() => handleUpdateStatus(selectedRepair.id, status.id)} className={`px-3 py-1 rounded-full text-sm transition-colors ${selectedRepair.status_id === status.id ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedRepair.status_id === status.id ? { backgroundColor: status.color } : {}}>{status.name}</button>))}</div></div>
            {selectedRepair.technician_notes && (<div className="mb-4"><h3 className="font-semibold mb-2">Notes techniques</h3><p className="text-gray-600 text-sm">{selectedRepair.technician_notes}</p></div>)}
            <div className="flex gap-3"><button onClick={() => handleUpdateStatus(selectedRepair.id, 5)} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"><FiCheckCircle size={18} />Marquer comme terminé</button></div>
          </div>
        </div>
      )}

      <PinModal
        isOpen={showPinModal}
        onClose={() => {
          setShowPinModal(false);
          setSelectedRepair(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer une réparation"
        message="Êtes-vous sûr de vouloir supprimer cette réparation ? Cette action est irréversible."
      />    
    </div>
  );
};

export default Repairs;